import { useState, useCallback } from 'react';
import { Path, UseFormReturn } from 'react-hook-form';
import { RoleRow, RoleFormValues } from '../../../types/role';
import { useRolesTable } from '../../../hooks/useRolesTable';
import {
  useGroups,
  useCreateGroup,
  useUpdateGroup,
  useDeleteGroup,
  useGroup,
} from '../../../hooks/queries';
import { GroupStatus } from '../../../services';
import { toaster } from '../../ui/toaster';
import { useSearchParams } from 'react-router-dom';
import { roleTransformGroups } from '../../../utils/roleTransform';
import { ApiValidationError } from '../../../lib/http-client';

const ROLE_FORM_FIELDS: ReadonlySet<Path<RoleFormValues>> = new Set([
  'name',
  'display_name',
  'description',
  'organization',
  'organization_id',
  'organization_name',
  'permissions',
  'permission_codenames',
  'status',
]);

export function useRolesContentData() {
  const [searchParams] = useSearchParams();
  const urlId = searchParams.get('id');
  const urlTab = searchParams.get('subtab');
  const isRolesTab = !urlTab || urlTab === 'roles';
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [actionMode, setActionMode] = useState<'add' | 'edit' | ''>('');
  const [currentRow, setCurrentRow] = useState<RoleRow>();
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [roleToClone, setRoleToClone] = useState<RoleRow | null>(null);

  const [statusFilter, setStatusFilter] = useState<GroupStatus>('all');
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);

  const [sortColumn, setSortColumn] = useState<string>();
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
    null,
  );
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});

  const handleFilterChange = useCallback((column: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [column]: value }));
  }, []);

  const handleFilterClear = useCallback((column: string) => {
    setFilterValues((prev) => {
      const next = { ...prev };
      delete next[column];
      return next;
    });
  }, []);

  const fieldMap: Record<string, string> = {
    display_name: 'name',
    organization: 'organization_name',
    members_count: 'user_count',
  };
  const mappedColumn = sortColumn
    ? (fieldMap[sortColumn] ?? sortColumn)
    : undefined;

  const ordering =
    mappedColumn && sortDirection
      ? `${sortDirection === 'desc' ? '-' : ''}${mappedColumn}`
      : undefined;

  const { data, isLoading, error } = useGroups({
    offset: (currentPage - 1) * pageSize,
    limit: pageSize,
    status: statusFilter,
    search: searchValue || undefined,
    ordering,
  });
  const { data: roleById, isError: roleByIdError } = useGroup(Number(urlId));
  const { roles, total } = useRolesTable({ data });
  const isSingleRoleView = Boolean(
    urlId && isRolesTab && roleById && !roleByIdError,
  );
  const roleDetails = isSingleRoleView
    ? roleTransformGroups([
        roleById as unknown as Parameters<typeof roleTransformGroups>[0][0],
      ])
    : roles;
  const handleSort = useCallback(
    (column: string) => {
      setCurrentPage(1);

      if (sortColumn !== column) {
        setSortColumn(column);
        setSortDirection('asc');
        return;
      }

      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortColumn(undefined);
        setSortDirection(null);
      } else {
        setSortDirection('asc');
      }
    },
    [sortColumn, sortDirection],
  );

  const createMutation = useCreateGroup();
  const updateMutation = useUpdateGroup();
  const deleteMutation = useDeleteGroup();

  const totalPages = Math.ceil(total / pageSize);

  const openAddDrawer = () => {
    setActionMode('add');
    setCurrentRow(undefined);
    setDrawerOpen(true);
  };

  const openEditDrawer = (row: RoleRow) => {
    setActionMode('edit');
    setCurrentRow(row);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setActionMode('');
    setCurrentRow(undefined);
  };

  const openCloneDialog = (row: RoleRow) => {
    setRoleToClone(row);
    setCloneDialogOpen(true);
    closeDrawer();
  };

  const closeCloneDialog = () => {
    setCloneDialogOpen(false);
    setRoleToClone(null);
  };

  const handleSubmit = async (
    values: RoleFormValues,
    form?: UseFormReturn<RoleFormValues>,
  ) => {
    try {
      const payload = {
        ...values,
        organization_id: values.organization_id
          ? Number(values.organization_id)
          : null,
      };

      if (actionMode === 'edit' && currentRow?.id) {
        await updateMutation.mutateAsync({
          id: Number(currentRow.id),
          data: payload,
        });
      } else {
        // organization_id is required by the zod schema for custom roles, so
        // payload.organization_id is guaranteed non-null when we reach here.
        await createMutation.mutateAsync({
          ...payload,
          organization_id: payload.organization_id as number,
        });
      }
      closeDrawer();
    } catch (err: unknown) {
      if (err instanceof ApiValidationError && form) {
        const remaining: string[] = [];
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          const message = Array.isArray(messages)
            ? messages.join(' ')
            : String(messages);
          if (ROLE_FORM_FIELDS.has(field as Path<RoleFormValues>)) {
            form.setError(field as Path<RoleFormValues>, {
              type: 'server',
              message,
            });
          } else {
            remaining.push(`${field}: ${message}`);
          }
        }
        if (remaining.length > 0) {
          toaster.create({
            title: 'Could not save role',
            description: remaining.join('; '),
            type: 'error',
            duration: 4000,
          });
        }
        return;
      }

      const message =
        err instanceof Error ? err.message : 'Something went wrong';

      toaster.create({
        title: 'Error',
        description: message,
        type: 'error',
        duration: 4000,
      });
    }
  };

  const handleDelete = async () => {
    if (!currentRow?.id) return;
    try {
      await deleteMutation.mutateAsync(Number(currentRow.id));
      closeDrawer();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Something went wrong';

      toaster.create({
        title: 'Error',
        description: message,
        type: 'error',
        duration: 4000,
      });
    }
  };

  return {
    // State
    drawerOpen,
    actionMode,
    currentRow,
    cloneDialogOpen,
    roleToClone,
    statusFilter,
    setStatusFilter,
    searchValue,
    setSearchValue,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    sortColumn,
    sortDirection,
    filterValues,

    // Data
    roles: roleDetails,
    total,
    totalPages,
    isLoading,
    error,

    // Handlers
    handleFilterChange,
    handleFilterClear,
    handleSort,
    openAddDrawer,
    openEditDrawer,
    closeDrawer,
    openCloneDialog,
    closeCloneDialog,
    handleSubmit,
    handleDelete,
  };
}
